import { Box, Chip, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import HistoryIcon from '@mui/icons-material/History';
import ReplayIcon from '@mui/icons-material/Replay';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { IncidentSummary } from '../../api/incidentLog';
import { linkedIncidentSummary } from '../../utils/incidentRekindle';

/**
 * Every incident this one is related to, as CLICKABLE chips: the fires it
 * rekindled (predecessor chain, nearest first), the incidents that rekindled
 * it, and the same fire on neighbouring projects. Replaces the old
 * tooltip-only badges — "silent rekindle of #109 that I can't find" was the
 * complaint, so #109 is now one click away.
 */
export default function IncidentLinkChips({
    incident,
    projectId,
    onSelectIncident,
    size = 'small',
}: {
    incident: IncidentSummary;
    projectId: string | number;
    /** Same-project navigation (select on the map / switch the modal).
     *  Cross-project links always navigate to that project's map. */
    onSelectIncident?: (incidentId: number) => void;
    size?: 'small' | 'medium';
}) {
    const navigate = useNavigate();
    const goSameProject = (id: number) => {
        if (onSelectIncident) onSelectIncident(id);
        else navigate(`/projects/${projectId}/incidents/${id}`);
    };
    const goOtherProject = (otherProjectId: number, id: number) =>
        navigate(`/projects/${otherProjectId}/incidents/${id}`);

    const chipSx = (color: string) => ({
        color,
        borderColor: `${color}99`,
        '& .MuiChip-icon': { color },
        '& .MuiChip-label': { fontSize: '0.72rem' },
        '&:hover': { backgroundColor: `${color}1a` },
    });

    const chain = incident.chain?.length
        ? incident.chain
        : incident.predecessor_id !== null
          ? [incident.predecessor_id]
          : [];
    const rekindles = incident.rekindles ?? [];
    const secondaries = incident.secondaries ?? [];
    const linked = linkedIncidentSummary(incident);
    // Monitored under another incident. Without this chip the card showed no
    // agent and no reason, and the only trace of the decision was a line in a
    // worker log — "why did nothing start monitoring this?" was the report.
    const coveredBy = incident.covered_by;

    // Closed and came back. Worth saying plainly: the reader is looking at an
    // incident they remember closing, and the alternative (a second, silent
    // incident beside it) is what this replaced.
    const reopened = !incident.closed_at && (incident.reopen_count ?? 0) > 0;

    if (
        !chain.length &&
        !rekindles.length &&
        incident.primary_id === null &&
        !secondaries.length &&
        !coveredBy &&
        !reopened
    ) {
        return null;
    }

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
            {chain.map((id, index) => (
                <Tooltip
                    key={`chain-${id}`}
                    title={
                        index === 0
                            ? `This incident is a re-detection at closed incident #${id}. Click to view it.`
                            : `#${chain[index - 1]} was itself a rekindle of #${id}. Click to view it.`
                    }
                >
                    <Chip
                        size={size}
                        clickable
                        variant="outlined"
                        icon={<HistoryIcon />}
                        label={index === 0 ? `Rekindle of #${id}` : `← #${id}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            goSameProject(id);
                        }}
                        sx={chipSx('#fbbf24')}
                    />
                </Tooltip>
            ))}
            {rekindles.map((id) => (
                <Tooltip
                    key={`rekindle-${id}`}
                    title={`Re-detections after this incident closed opened incident #${id}. Click to view it.`}
                >
                    <Chip
                        size={size}
                        clickable
                        variant="outlined"
                        icon={<ReplayIcon />}
                        label={`Rekindled as #${id}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            goSameProject(id);
                        }}
                        sx={chipSx('#f97316')}
                    />
                </Tooltip>
            ))}
            {incident.primary_id !== null && (
                <Tooltip title={linked ?? ''}>
                    <Chip
                        size={size}
                        clickable={!!incident.primary_project}
                        variant="outlined"
                        icon={<LinkIcon />}
                        label={`Linked to #${incident.primary_id}${
                            incident.primary_project ? ` · ${incident.primary_project.name}` : ''
                        }`}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (incident.primary_project && incident.primary_id !== null) {
                                goOtherProject(incident.primary_project.id, incident.primary_id);
                            }
                        }}
                        sx={chipSx('#7dd3fc')}
                    />
                </Tooltip>
            )}
            {reopened && (
                <Tooltip
                    title={`This incident was closed and reopened${
                        incident.reopen_count > 1 ? ` ${incident.reopen_count} times` : ''
                    } — detections came back at the same fire, so it was handed back to its agent instead of opening a separate incident.`}
                >
                    <Chip
                        size={size}
                        variant="outlined"
                        icon={<ReplayIcon />}
                        label={
                            incident.reopen_count > 1
                                ? `Reopened x${incident.reopen_count}`
                                : 'Reopened'
                        }
                        sx={chipSx('#34d399')}
                    />
                </Tooltip>
            )}
            {coveredBy && (
                <Tooltip
                    title={
                        incident.link_override === 'same_fire'
                            ? `${incident.link_override_by ?? 'Someone'} marked this as the same fire as #${coveredBy.id}, which is being monitored. Click to view it.`
                            : `This fire is already being monitored on incident #${coveredBy.id}${
                                  coveredBy.project_name ? ` (${coveredBy.project_name})` : ''
                              }, so no second agent was started for it. Click to view it.`
                    }
                >
                    <Chip
                        size={size}
                        clickable
                        variant="outlined"
                        icon={<VisibilityIcon />}
                        label={`Monitored on #${coveredBy.id}${
                            coveredBy.project_id !== Number(projectId)
                                ? ` · ${coveredBy.project_name}`
                                : ''
                        }`}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (coveredBy.project_id !== Number(projectId)) {
                                goOtherProject(coveredBy.project_id, coveredBy.id);
                            } else {
                                goSameProject(coveredBy.id);
                            }
                        }}
                        sx={chipSx('#a78bfa')}
                    />
                </Tooltip>
            )}
            {incident.link_override === 'different_fire' && (
                <Tooltip
                    title={`${incident.link_override_by ?? 'Someone'} marked this as its own separate fire, so it is monitored on its own rather than folded into a neighbouring incident.`}
                >
                    <Chip
                        size={size}
                        variant="outlined"
                        icon={<LinkOffIcon />}
                        label="Separate fire"
                        sx={chipSx('#94a3b8')}
                    />
                </Tooltip>
            )}
            {secondaries.map((s) => (
                <Tooltip key={`secondary-${s.id}`} title={linked ?? ''}>
                    <Chip
                        size={size}
                        clickable
                        variant="outlined"
                        icon={<LinkIcon />}
                        label={`#${s.id} · ${s.project_name}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            goOtherProject(s.project_id, s.id);
                        }}
                        sx={chipSx('#7dd3fc')}
                    />
                </Tooltip>
            ))}
        </Box>
    );
}
